import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useCallback, useState } from "react";

import { getDataFromResponse, handleApiError } from "~/api/axiosUtils.ts";
import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import { useAllCareerLevels } from "~/modules/humanResources/api/careerLevel/careerLevelQueries.ts";
import { useAllStaffMembers } from "~/modules/humanResources/api/staffMember/staffMemberQueries.ts";
import { recalculateYearlyUtilisationReport } from "~/modules/reports/api/utilisationReport/utilisationReportApiDispatchers.ts";
import { utilisationReportQueries } from "~/modules/reports/api/utilisationReport/utilisationReportQueries.ts";
import TotalUtilisationChartSection
	from "~/modules/reports/components/GlobalReportsView/components/UtilisationReportSection/components/TotalUtilisationChartSection";
import TotalUtilisationTableSection
	from "~/modules/reports/components/GlobalReportsView/components/UtilisationReportSection/components/TotalUtilisationTableSection";
import type { UtilisationReportDeductiblesProperty } from "~/modules/reports/types/utilistationReportViewTypes.ts";
import { defaultDeductibles } from "~/modules/reports/utils/utilisationReportUtils.ts";
import { useAllTimeTrackingTypes } from "~/modules/timeTracking/api/timeTrackingType/timeTrackingTypeQueries.ts";
import LoadingPage from "~/pages/LoadingPage.tsx";

type Props = {
	year: string;
};

const currentMonth = (new Date().getMonth() + 1);
const monthToDisplay = currentMonth === 1 ? "" : (currentMonth - 1).toString();

const UtilisationReportSection: FunctionComponent<Props> = ({ year }) => {
	const [selectedDeductibles, setSelectedDeductibles] = useState<UtilisationReportDeductiblesProperty[]>(defaultDeductibles);
	const [selectedMonth, setSelectedMonth] = useState<string>(monthToDisplay);
	const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
	const queryClient = useQueryClient();
	const { data, options } = useAggregatedDataV2({
		careerLevels: useAllCareerLevels(),
		staffMembers: useAllStaffMembers(),
		timeTrackingTypes: useAllTimeTrackingTypes(),
		utilisationReport: useQuery(utilisationReportQueries.yearlyTotal(year || "")),
	});

	const {
		utilisationReport,
	} = data;

	const { isLoading, loadingPercent } = options;

	const handleRefresh = useCallback(async () => {
		setIsRefreshing(true);
		try {
			const freshReport = getDataFromResponse(await recalculateYearlyUtilisationReport(year));
			queryClient.setQueryData(utilisationReportQueries.yearlyTotal(year).queryKey, freshReport);
		} catch (error) {
			handleApiError(error);
		} finally {
			setIsRefreshing(false);
		}
	}, [queryClient, year]);

	return <div className="grid size-full grid-rows-[auto_1fr] gap-y-8 overflow-hidden pb-8">
		{isLoading && <LoadingPage pcent={loadingPercent} />}
		{!isLoading && <>
			<TotalUtilisationChartSection utilisationReportData={utilisationReport}
													   selectedDeductibles={selectedDeductibles}
													   selectedMonth={selectedMonth}
													   setSelectedMonth={setSelectedMonth}
													   setSelectedDeductibles={setSelectedDeductibles}
													   year={year} />
			<TotalUtilisationTableSection utilisationReportData={utilisationReport}
										  selectedDeductibles={selectedDeductibles}
										  selectedMonth={selectedMonth}
										  setSelectedMonth={setSelectedMonth}
										  generatedAt={utilisationReport.generatedAt}
										  isRefreshing={isRefreshing}
										  onRefresh={handleRefresh}
										  year={year} />
		</>}
	</div>;
};

export default UtilisationReportSection;